import React from 'react';
import cn from 'classnames';
import { useField } from 'informed';

import { Picker } from '@nw/omega-design-system';
import { HelperTextStyledComponent } from '@nw/shared/ui';

import { FieldContainer } from './styledComponents';

interface PickerFieldPropTypes {
  label: string;
  field: string;
  error: string;
  touched: boolean;
  isDisabled: boolean;
  validationSchemaObject: any;
  options: Array<any>;
  placeholder: string;
  isHidden?: boolean;
  fieldWrapperStyles?: string;
  wrapperWithPopOverStyles?: string;
  popOverStyle?: string;
  fieldContainerStyles?: string;
  customOption?: any;
  inputGroupStyles?: any;
  buttonStyles?: any;
  inputFieldStyles?: string;
  labelStyles?: string;
  listBoxStyles?: string;
  headerClassName?: string;
  containerClassName?: string;
  wrapperComponent?: any;
  hasHeader?: boolean;
  hideCloseIcon?: boolean;
  reRenderObjects?: Array<any>;
  helperText?: string;
  overlayOptions?: any;
  onChangeOption?: (value) => void;
  handleOnBlur?: any;
  helperTextStyles?: string;
  validateOnBlur?: boolean;
  isSearchable?: boolean;
  isMarginBottomZero?: boolean;
  errorMessageStyles?: string;
  enableDataMask?: boolean;
  handleFocus?: () => void;
}

const InformedPickerField = (props: PickerFieldPropTypes) => {
  const {
    label,
    field,
    error,
    touched,
    isDisabled,
    validationSchemaObject,
    isHidden,
    fieldWrapperStyles,
    inputGroupStyles,
    wrapperWithPopOverStyles,
    fieldContainerStyles,
    buttonStyles,
    options,
    placeholder,
    customOption,
    inputFieldStyles,
    labelStyles,
    listBoxStyles,
    headerClassName,
    containerClassName,
    wrapperComponent,
    hasHeader,
    hideCloseIcon,
    reRenderObjects,
    helperText,
    overlayOptions,
    onChangeOption,
    handleOnBlur,
    helperTextStyles,
    validateOnBlur = true,
    isSearchable,
    isMarginBottomZero,
    errorMessageStyles,
    enableDataMask,
    handleFocus
  } = props;

  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: true,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    ...reRenderObjects
  });

  const { value } = fieldState;
  const { onChange, onBlur } = informed;
  const { setValue, setTouched } = fieldApi;

  const onBlurFunction = (e) => {
    onBlur(e);
    handleOnBlur && handleOnBlur();
  };

  const ListElement = (props) => {
    const {
      item: { label }
    } = props;
    return <div>{label}</div>;
  };

  const getListItemKey = (item: { label: string; value: string }) => item.value;
  const getListItemValue = (item: { label: string; value: string }) =>
    item.value;

  const renderHelperText = () => {
    return helperText && !error ? (
      <HelperTextStyledComponent helperText={helperText} />
    ) : null;
  };

  const getHelperTextClassName = (): string => {
    if (error) {
      return `${cn('h-min-20px')}`;
    } else if (helperText) {
      return ` ${helperTextStyles} customPickerHintClassName`;
    }
    return '';
  };

  const renderPickerField = () => (
    <>
      <Picker
        label={label}
        options={options}
        placeholder={placeholder}
        onSelectionChange={(value: string) => {
          setValue(value);
          onChange(value);
          setTouched(true);
          onChangeOption && onChangeOption(value);
        }}
        error={error}
        getListItemKey={getListItemKey}
        getListItemValue={getListItemValue}
        selectedKey={value?.toString() as string}
        renderListView={ListElement}
        fieldContainerStyles={fieldContainerStyles}
        wrapperWithPopOverStyles={wrapperWithPopOverStyles}
        inputGroupStyles={inputGroupStyles}
        buttonStyles={buttonStyles}
        customOption={customOption}
        isDisabled={isDisabled}
        inputFieldStyles={inputFieldStyles}
        labelStyles={labelStyles}
        listBoxStyles={listBoxStyles}
        headerClassName={headerClassName}
        containerClassName={containerClassName}
        wrapperComponent={wrapperComponent}
        hasHeader={hasHeader}
        hideCloseIcon={hideCloseIcon}
        popOverStyles={cn('max-h-150px')}
        onBlur={onBlurFunction}
        overlayOptions={overlayOptions}
        helperTextStyles={getHelperTextClassName()}
        isSearchable={isSearchable}
        errorMessageStyles={errorMessageStyles}
        enableDataMask={enableDataMask}
        handleFocus={handleFocus}
      />
      {renderHelperText()}
    </>
  );

  return render(
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderPickerField()}
    </FieldContainer>
  );
};

export { InformedPickerField };
